// ✅ src/pages/VehicleV2/VehicleBreakdownDetailReport.jsx
// UPDATED:
// 1) localStorage values added
// 2) apiHeaders added
// 3) all axios calls now send backend-matching headers
// 4) default branch comes from localStorage branchId
// 5) keeps existing UI / filters / print flow

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FaSearch,
  FaEye,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaInfoCircle,
  FaFileExport,
} from "react-icons/fa";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/vehicleBreakdownDetailReport";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();
const isSelect = (v) => {
  const s = toStr(v);
  return !s || s.toLowerCase() === "select";
};

const exportToCsv = (rows, filename = "vehicle-idle-breakdown-report.csv") => {
  if (!rows?.length) return;

  const safe = rows.map((r) => {
    const c = { ...r };
    delete c._row;
    return c;
  });

  const headers = Object.keys(safe[0] || {});
  const esc = (val) => {
    const s = String(val ?? "");
    if (s.includes('"') || s.includes(",") || s.includes("\n"))
      return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const csv = [
    headers.join(","),
    ...safe.map((r) => headers.map((h) => esc(r[h])).join(",")),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const monthOptions = [
  "Select",
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const buildYearOptions = () => {
  const now = new Date().getFullYear();
  const yrs = ["Select"];
  for (let y = now; y >= now - 25; y--) yrs.push(String(y));
  return yrs;
};

export default function VehicleBreakdownDetailReport({
  defaultBranchId = "Select",
}) {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const sessionBranchId = localStorage.getItem("branchId") || "";
  const sessionBranchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  // ✅ backend-matching headers
  const apiHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": sessionBranchId,
      "x-branchtype": sessionBranchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, sessionBranchId, sessionBranchType, userId]
  );

  const [showFilters, setShowFilters] = useState(true);
  const [branches, setBranches] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    vehicleBasis: "OWN",
    month: "Select",
    year: "Select",
    branchId: toStr(sessionBranchId || defaultBranchId) || "Select",
    regNo: "",
    vehicleCode: "",
    vehicleName: "",
    fromDate: "",
    toDate: "",
    vehicleTypeId: "Select",
    status: "Select",
  });

  const [rows, setRows] = useState([]);

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const handleChange = (e) =>
    setFilters((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleBasisChange = (basis) =>
    setFilters((p) => ({ ...p, vehicleBasis: basis }));

  /* ---------------- load dropdowns ---------------- */
  const loadBranches = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/branches`, {
        headers: apiHeaders,
      });
      if (!data?.success) throw new Error(data?.message || "Failed");
      setBranches(data.rows || []);
    } catch (err) {
      toast.error(err.message || "Failed to load branches");
    }
  };

  const loadVehicleTypes = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/vehicleTypes`, {
        headers: apiHeaders,
      });
      if (!data?.success) throw new Error(data?.message || "Failed");
      setVehicleTypes(data.rows || []);
    } catch (err) {
      toast.error(err.message || "Failed to load vehicle types");
    }
  };

  /* ---------------- fetch list ---------------- */
  const buildListUrl = (f) => {
    const params = new URLSearchParams();

    params.set("vehicleBasis", toStr(f.vehicleBasis || "OWN"));
    params.set("month", isSelect(f.month) ? "Select" : toStr(f.month));
    params.set("year", isSelect(f.year) ? "Select" : toStr(f.year));
    params.set("branchId", isSelect(f.branchId) ? "Select" : toStr(f.branchId));
    params.set("regNo", toStr(f.regNo));
    params.set("vehicleCode", toStr(f.vehicleCode));
    params.set("vehicleName", toStr(f.vehicleName));
    params.set("fromDate", toStr(f.fromDate));
    params.set("toDate", toStr(f.toDate));
    params.set(
      "vehicleTypeId",
      isSelect(f.vehicleTypeId) ? "Select" : toStr(f.vehicleTypeId)
    );
    params.set("status", isSelect(f.status) ? "Select" : toStr(f.status));

    return `${API_BASE}/list?${params.toString()}`;
  };

  const loadList = async (f) => {
    setLoading(true);
    try {
      const url = buildListUrl(f);
      const { data } = await axios.get(url, {
        headers: apiHeaders,
      });
      if (!data?.success) throw new Error(data?.message || "Failed to load list");
      setRows(data.rows || []);
      toast.success(`Found ${data?.count ?? (data.rows || []).length} records`);
    } catch (err) {
      setRows([]);
      toast.error(err.message || "List fetch failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBranches();
    loadVehicleTypes();
    loadList(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiHeaders]);

  /* ---------------- actions ---------------- */
  const handleSearch = () => loadList(filters);

  const handleViewAll = () => {
    const reset = {
      vehicleBasis: filters.vehicleBasis || "OWN",
      month: "Select",
      year: "Select",
      branchId: "Select",
      regNo: "",
      vehicleCode: "",
      vehicleName: "",
      fromDate: "",
      toDate: "",
      vehicleTypeId: "Select",
      status: "Select",
    };
    setFilters(reset);
    loadList(reset);
  };

  const handleClear = () => {
    setFilters((p) => ({
      ...p,
      month: "Select",
      year: "Select",
      branchId: "Select",
      regNo: "",
      vehicleCode: "",
      vehicleName: "",
      fromDate: "",
      toDate: "",
      vehicleTypeId: "Select",
      status: "Select",
    }));
    toast.info("Filters cleared");
  };

  const handleExport = () => exportToCsv(rows);

  const handlePrintAll = () => {
    const params = new URLSearchParams();
    params.set("vehicleBasis", filters.vehicleBasis || "OWN");
    params.set("month", filters.month || "Select");
    params.set("year", filters.year || "Select");
    params.set("branchId", filters.branchId || "Select");
    params.set("regNo", filters.regNo || "");
    params.set("vehicleCode", filters.vehicleCode || "");
    params.set("vehicleName", filters.vehicleName || "");
    params.set("fromDate", filters.fromDate || "");
    params.set("toDate", filters.toDate || "");
    params.set("vehicleTypeId", filters.vehicleTypeId || "Select");
    params.set("status", filters.status || "Select");

    navigate(`/vehicle/VehicleBreakdownDetailsReport_Print?${params.toString()}`);
  };

  const yearOptions = useMemo(() => buildYearOptions(), []);

  /* ---------------- dropdown mappers ---------------- */
  const branchOptions = useMemo(() => {
    return (branches || [])
      .map((b) => ({
        value: toStr(b.fld_branchid ?? b.branchid ?? b.BranchId ?? b.id ?? b.value),
        label: toStr(
          b.fld_BranchName ?? b.fld_branchname ?? b.branchname ?? b.BranchName ?? b.label
        ),
      }))
      .filter((o) => o.value || o.label);
  }, [branches]);

  const vehicleTypeOptions = useMemo(() => {
    return (vehicleTypes || [])
      .map((v) => ({
        value: toStr(
          v.fld_vehiclemaintypeid ??
            v.fld_vehicletypeid ??
            v.vehicleTypeId ??
            v.id ??
            v.value
        ),
        label: toStr(
          v.fld_vehiclemaintypename ??
            v.fld_vehicletypename ??
            v.fld_vehicletypeindex ??
            v.VehicleTypeName ??
            v.vehicleTypeName ??
            v.label
        ),
      }))
      .filter((o) => o.label);
  }, [vehicleTypes]);

  /* ---------------- row normalizer ---------------- */
  const normRows = useMemo(() => {
    return (rows || []).map((r, idx) => {
      const pick = (...keys) => {
        for (const k of keys) {
          const v = r?.[k];
          if (v !== undefined && v !== null && String(v).trim() !== "") return v;
        }
        return "";
      };

      return {
        sno: r.sno ?? idx + 1,
        branch: pick("fld_branchname", "Branch", "branch"),
        vehicleMainType: pick("VehicleType", "vehicleType", "VehicleMainType", "mainType"),
        vehicleName: pick("fld_vehicle_name", "VehicleName", "vehicleName", "name"),
        vehicleCode: pick("fld_vehicle_code", "VehicleCode", "vehicleCode", "code"),
        vehicleRegno: pick("fld_vehicle_regno", "VehicleRegNo", "regNo"),
        status: pick("fld_status", "Status", "status"),
        fromDate: pick("fld_fromdate", "FromDate", "fromDate"),
        toDate: pick("fld_todate", "ToDate", "toDate"),
        breakdownDays: pick("fld_breakdowndays", "BreakdownDays", "breakdownDays"),
        totalDays: pick("fld_totaldays", "TotalDays", "totalDays"),
        runningHr: pick("Whrs", "RunningHr", "runningHr"),
        cumulativeHr: pick("cumulativehrs", "CummulativeHr", "cumulativeHr"),
        runningKm: pick("WKM", "RunningKm", "runningKm"),
        cumulativeKm: pick("cumulativekm", "CummulativeKm", "cumulativeKm"),
        lastUpdatedDate: pick("fld_updateddate", "LastUpdatedDate", "updatedDate"),
        _row: r,
      };
    });
  }, [rows]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE IDLE / BREAKDOWN REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-8">
            <div className="flex items-center gap-4 flex-wrap">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle Idle / Breakdown Report
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {normRows.length} Records
              </span>

              <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                INFRA SCREEN : B-57
              </span>

              {loading && (
                <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </span>
              )}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => setShowFilters((s) => !s)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleExport}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaFileExport className="w-4 h-4" />
                Export CSV
              </button>

              <button
                onClick={handlePrintAll}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaPrint className="w-4 h-4" />
                Print
              </button>

              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3
                className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                <FaFilter /> FILTERS
              </h3>

              <div className="flex items-center gap-6 mb-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="vehicleBasis"
                    checked={filters.vehicleBasis === "OWN"}
                    onChange={() => handleBasisChange("OWN")}
                  />
                  <span className={`${isDark ? "text-gray-200" : "text-gray-800"}`}>
                    Own Vehicle
                  </span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="vehicleBasis"
                    checked={filters.vehicleBasis === "HIRE"}
                    onChange={() => handleBasisChange("HIRE")}
                  />
                  <span className={`${isDark ? "text-gray-200" : "text-gray-800"}`}>
                    Hire Vehicle
                  </span>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <label className={labelClass}>Month</label>
                  <select name="month" value={filters.month} onChange={handleChange} className={inputClass}>
                    {monthOptions.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Year</label>
                  <select name="year" value={filters.year} onChange={handleChange} className={inputClass}>
                    {yearOptions.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    name="branchId"
                    value={filters.branchId}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="Select">Select</option>
                    {branchOptions.map((b) => (
                      <option key={`${b.value}-${b.label}`} value={b.value}>
                        {b.label || b.value}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    type="text"
                    name="vehicleCode"
                    value={filters.vehicleCode}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Vehicle Code"
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Registration Number</label>
                  <input
                    type="text"
                    name="regNo"
                    value={filters.regNo}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Vehicle Reg.No"
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    type="text"
                    name="vehicleName"
                    value={filters.vehicleName}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Vehicle Name"
                  />
                </div>

                <div>
                  <label className={labelClass}>From Date</label>
                  <input
                    type="date"
                    name="fromDate"
                    value={filters.fromDate}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>To Date</label>
                  <input
                    type="date"
                    name="toDate"
                    value={filters.toDate}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Main Type</label>
                  <select
                    name="vehicleTypeId"
                    value={filters.vehicleTypeId}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="Select">Select</option>
                    {vehicleTypeOptions.map((v) => (
                      <option key={`${v.value}-${v.label}`} value={v.value}>
                        {v.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Status</label>
                  <select
                    name="status"
                    value={filters.status}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="Select">Select</option>
                    <option value="Running">Running</option>
                    <option value="Idle">Idle</option>
                    <option value="Breakdown">Breakdown</option>
                  </select>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 gap-4">
                <button
                  onClick={handleClear}
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium"
                >
                  Clear All Filters
                </button>

                <div className="flex gap-4">
                  <button
                    onClick={handleViewAll}
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold shadow-md"
                    disabled={loading}
                  >
                    <FaEye /> View All
                  </button>

                  <button
                    onClick={handleSearch}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-md`}
                    disabled={loading}
                  >
                    <FaSearch /> Search
                  </button>
                </div>
              </div>
            </div>
          )}

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {normRows.length === 0 ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaInfoCircle className="w-10 h-10 text-gray-400" />
                </div>
                <h3
                  className={`text-xl font-semibold mb-2 ${
                    isDark ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  No Records Found
                </h3>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-3 text-center">S.No</th>
                      <th className="px-4 py-3 text-center">Branch</th>
                      <th className="px-4 py-3 text-center">Vehicle Main type</th>
                      <th className="px-4 py-3 text-center">Vehicle Name</th>
                      <th className="px-4 py-3 text-center">Vehicle Code</th>
                      <th className="px-4 py-3 text-center">Vehicle Regno</th>
                      <th className="px-4 py-3 text-center">Status</th>
                      <th className="px-4 py-3 text-center">From Date</th>
                      <th className="px-4 py-3 text-center">To Date</th>
                      <th className="px-4 py-3 text-center">Breakdown/Running/Idle Days</th>
                      <th className="px-4 py-3 text-center">Total Days</th>
                      <th className="px-4 py-3 text-center">Running Hr</th>
                      <th className="px-4 py-3 text-center">Cummulative Hr</th>
                      <th className="px-4 py-3 text-center">Running Km</th>
                      <th className="px-4 py-3 text-center">Cummulative KM</th>
                      <th className="px-4 py-3 text-center">Last Updated Date</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {normRows.map((r, idx) => (
                      <tr
                        key={`${r.vehicleCode || "row"}-${idx}`}
                        className={`${isDark ? "bg-gray-800" : "bg-white"} ${
                          isDark ? "hover:bg-gray-700" : "hover:bg-gray-50"
                        }`}
                      >
                        <td className="px-4 py-3 text-center">{r.sno}</td>
                        <td className="px-4 py-3 text-center">{r.branch}</td>
                        <td className="px-4 py-3 text-center">{r.vehicleMainType}</td>
                        <td className="px-4 py-3 text-center">{r.vehicleName}</td>
                        <td className="px-4 py-3 text-center">{r.vehicleCode}</td>
                        <td className="px-4 py-3 text-center">{r.vehicleRegno}</td>
                        <td className="px-4 py-3 text-center">{r.status}</td>
                        <td className="px-4 py-3 text-center">{r.fromDate}</td>
                        <td className="px-4 py-3 text-center">{r.toDate}</td>
                        <td className="px-4 py-3 text-center">{r.breakdownDays}</td>
                        <td className="px-4 py-3 text-center">{r.totalDays}</td>
                        <td className="px-4 py-3 text-center">{r.runningHr}</td>
                        <td className="px-4 py-3 text-center">{r.cumulativeHr}</td>
                        <td className="px-4 py-3 text-center">{r.runningKm}</td>
                        <td className="px-4 py-3 text-center">{r.cumulativeKm}</td>
                        <td className="px-4 py-3 text-center">{r.lastUpdatedDate}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}